import type { Router } from '@angular/router';
import type {
  PaidWallContext,
  PaidWallFeatureId,
} from '@indutrust/shared-model';
import {
  DEFAULT_PAID_WALL_CONTEXT,
  inferPaidWallFamily,
  isPaidWallContext,
  isPaidWallFeatureId,
} from '@indutrust/shared-model';

export const PAID_WALL_CONTACT_US_PATH = '/contact-us';
export const PAID_WALL_SALES_DEMO_SUBJECT = 'sales_demo' as const;

/** Human-readable labels for contact-form prefill messages. */
export const PAID_WALL_FEATURE_LABELS: Record<PaidWallFeatureId, string> = {
  search: 'Search',
  projects: 'Projects',
  'map-keyword': 'Map keyword search',
  'map-advanced-filters': 'Map advanced filters',
  'dashboard-breakdown': 'Dashboard breakdown',
  reporting: 'Reporting',
  'add-to-project': 'Add to project',
};

/** Resolves route `data.paidWall` with a generic sales-demo fallback. */
export function resolvePaidWallContext(routeContext: unknown): PaidWallContext {
  if (isPaidWallContext(routeContext)) {
    return routeContext;
  }

  return DEFAULT_PAID_WALL_CONTEXT;
}

/** Builds `/contact-us` query params for a paid-wall upsell CTA. */
export function buildPaidWallContactUsQueryParams(
  context: PaidWallContext,
  returnUrl?: string
): Record<string, string> {
  const params: Record<string, string> = {
    subject_category: PAID_WALL_SALES_DEMO_SUBJECT,
    wall: context.featureId,
  };

  if (returnUrl) {
    params['returnUrl'] = returnUrl;
  }

  return params;
}

/** Builds a concise, editable sales-demo message from wall context. */
export function buildPaidWallPrefillMessage(
  context: PaidWallContext,
  returnUrl?: string
): string {
  const featureLabel = PAID_WALL_FEATURE_LABELS[context.featureId];
  const location = returnUrl ? ` at ${returnUrl}` : '';
  return `I hit the ${featureLabel} paid wall${location}.`;
}

/** Reconstructs wall context from the `wall` query param. */
export function paidWallContextFromFeatureId(
  featureId: string | undefined
): PaidWallContext | null {
  if (!featureId || !isPaidWallFeatureId(featureId)) {
    return null;
  }

  return {
    family: inferPaidWallFamily(featureId),
    featureId,
  };
}

/**
 * Navigates to the sales-demo contact form with paid-wall attribution.
 * Uses `queryParamsHandling: 'merge'` so attribution query params are applied
 * alongside any params already on the current URL.
 */
export function navigateToPaidWallContactUs(
  router: Router,
  context: PaidWallContext,
  returnUrl?: string,
  contextMessage = 'Navigation to paid-wall contact us failed'
): void {
  router
    .navigate([PAID_WALL_CONTACT_US_PATH], {
      queryParams: buildPaidWallContactUsQueryParams(context, returnUrl),
      queryParamsHandling: 'merge',
    })
    .catch((error: unknown) => {
      console.error(`${contextMessage}:`, error);
    });
}
